/** Maps measured RFIMS metrics, preserving source timestamps and leaving health unassessed. */
import type { Reading, StatusColour } from '../types';
import type { LiveSite, LiveSiteMetrics, LiveStatusPayload } from './types';
import { f200State } from '../../rfims/f200';

export const LIVE_SITES = ['rfims00', 'rfims01', 'rfims02', 'rfims10'] as const;
export type LiveSiteId = (typeof LIVE_SITES)[number];

export function isLiveSite(elementId: string | null | undefined): elementId is LiveSiteId {
  return typeof elementId === 'string' && (LIVE_SITES as readonly string[]).includes(elementId);
}

export function num(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}

// Formatters, as the original bridge wrote them (the history graph parses the number back out).
export const fmtTemp = (v: number): string => v.toFixed(1) + '°C';
export const fmtPct = (v: number): string => Math.round(v) + '%';
export const fmtLoad = (v: number): string => v.toFixed(2);
export const fmtMs = (v: number): string => Math.round(v) + ' ms';
export const gb = (mib: number): string => (mib / 1024).toFixed(1);
export function fmtUptime(minutes: number): string {
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  return d + 'd ' + h + 'h';
}

export function diskPercent(usedMib: number, totalMib: unknown): number | null {
  return num(totalMib) && totalMib > 0 ? Math.round((usedMib / totalMib) * 100) : null;
}
export function fmtDisk(usedMib: number, totalMib: unknown): string {
  const pct = diskPercent(usedMib, totalMib);
  if (pct !== null && num(totalMib)) return gb(usedMib) + ' / ' + gb(totalMib) + ' GiB (' + pct + '%)';
  return gb(usedMib) + ' GiB';
}

export interface BridgeItem {
  /** Card (subsystem id in the rfims element JSON). */
  sub: string;
  /** Data item id in that card. */
  id: string;
  /** Source metric for single-metric items; derived items list their inputs. */
  metric?: keyof LiveSiteMetrics;
  inputs: Array<keyof LiveSiteMetrics>;
  unit?: string;
  /** True when the payload carries what this item needs. */
  has: (m: LiveSiteMetrics) => boolean;
  fmt: (m: LiveSiteMetrics) => string;
  numeric: (m: LiveSiteMetrics) => number | null;
}

function single(
  metric: keyof LiveSiteMetrics,
  sub: string,
  id: string,
  fmt: (v: number, m: LiveSiteMetrics) => string,
  unit?: string,
): BridgeItem {
  return {
    sub,
    id,
    metric,
    inputs: [metric],
    unit,
    has: (m) => num(m[metric]),
    fmt: (m) => fmt(m[metric] as number, m),
    numeric: (m) => m[metric] as number,
  };
}

/** The mapping table, in the original order. */
export const ITEMS: readonly BridgeItem[] = [
  single(
    'temperature_c',
    'environmental',
    'temperature',
    fmtTemp,
    '°C',
  ),
  {
    sub: 'environmental',
    id: 'temp_limits',
    inputs: ['temp_min_c', 'temp_max_c'],
    unit: '°C',
    has: (m) => num(m.temp_min_c) && num(m.temp_max_c),
    fmt: (m) => (m.temp_min_c as number).toFixed(1) + ' - ' + (m.temp_max_c as number).toFixed(1) + '°C',
    numeric: () => null,
  },
  single(
    'humidity_pct',
    'environmental',
    'humidity',
    fmtPct,
    '%',
  ),
  {
    sub: 'environmental',
    id: 'humidity_limits',
    inputs: ['humidity_min_pct', 'humidity_max_pct'],
    unit: '%',
    has: (m) => num(m.humidity_min_pct) && num(m.humidity_max_pct),
    fmt: (m) =>
      Math.round(m.humidity_min_pct as number) + ' - ' + Math.round(m.humidity_max_pct as number) + '%',
    numeric: () => null,
  },
  single('door_open', 'environmental', 'door', (v) => f200State('input', v)),
  single('leak_alarm', 'environmental', 'leak', (v) => f200State('leak', v)),
  single(
    'relay_state',
    'environmental',
    'relay',
    (v) => f200State('relay', v),
  ),
  single('load_5min', 'host_health', 'cpu_load', fmtLoad),
  {
    sub: 'host_health',
    id: 'disk_usage',
    inputs: ['disk_used_mib'], unit: 'GiB',
    has: (m) => num(m.disk_used_mib),
    fmt: (m) => fmtDisk(m.disk_used_mib as number, m.disk_total_mib),
    numeric: (m) => (m.disk_used_mib as number) / 1024,
  },
  single('uptime_min', 'host_health', 'uptime', fmtUptime, 'min'),
  single('ping_latency_ms', 'network', 'latency', fmtMs, 'ms'),
  single('ping_packet_loss_pct', 'network', 'packet_loss', fmtPct, '%'),
];

export function cardLabel(st: StatusColour): 'Alarm' | 'Degraded' | 'Nominal' {
  return st === 'red' ? 'Alarm' : st === 'yellow' ? 'Degraded' : 'Nominal';
}

export interface MappedSite {
  /** One Reading per measured item, all source 'live'. */
  readings: Reading[];
  /** Empty for RFIMS: no operational health is inferred. */
  cards: Record<string, StatusColour>;
  /** The site's lastSeen as ms, or null when it has none. */
  lastSeenMs: number | null;
}

/** ms since epoch of an ISO time, or null when missing or unparseable. */
export function parseLastSeen(lastSeen: string | null | undefined): number | null {
  if (!lastSeen) return null;
  const ms = Date.parse(lastSeen);
  return Number.isNaN(ms) ? null : ms;
}

/** Missing timestamps or explicit identity conflicts cannot produce readings. */
export function mapSiteReadings(site: LiveSite, nowMs: number, source: 'live' | 'sim' = 'live'): MappedSite {
  const readings: Reading[] = [];
  if (source !== 'live' || site.identityStatus === 'conflict') return { readings, cards: {}, lastSeenMs: null };
  const m = site.metrics ?? ({} as LiveSiteMetrics);
  for (const it of ITEMS) {
    if (!it.has(m)) continue;
    const times = it.inputs.map(key => parseLastSeen(site.metricTimes?.[key]));
    if (times.some(t => t === null || t > nowMs + 30_000)) continue;
    const timeMs = Math.min(...times as number[]);
    readings.push({
      subsystemId: it.sub, dataId: it.id, value: it.fmt(m), numericValue: it.numeric(m),
      unit: it.unit, status: 'green', assessment: 'unassessed', timeMs, source: 'live',
      freshnessMs: Math.max(0, nowMs - timeMs),
    });
  }
  return { readings, cards: {}, lastSeenMs: readings.length ? Math.max(...readings.map(r => r.timeMs)) : null };
}

export function findSite(payload: LiveStatusPayload, siteId: string): LiveSite | undefined {
  return Array.isArray(payload.sites) ? payload.sites.find((s) => s && s.siteId === siteId) : undefined;
}
